import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DENTAL_SPECIALTIES } from '../lib/constants';
import { generateConsumableUID } from '../lib/helpers';
import ConsumableFullLabel from '../components/ConsumableFullLabel';
import ConsumableCompactLabel from '../components/ConsumableCompactLabel';
import BlankConsumableSheet from '../components/BlankConsumableSheet';
import CameraScanner from '../components/CameraScanner';
import {
  fetchConsumableTransfers,
  confirmSubWarehouseReceipt,
  approveAndDispenseFromSub,
  createConsumableTransfer,
  fetchConsumableQRItems,
  updateConsumableItemStatus
} from '../lib/consumableTransferHelper';

// Default image parser if not provided
const defaultParseImages = (imageUrlField) => {
  if (!imageUrlField) return { full: '', zoom: '', other: '', document: '' };
  if (typeof imageUrlField === 'object') {
    return {
      full: imageUrlField.full || '',
      zoom: imageUrlField.zoom || '',
      other: imageUrlField.other || '',
      document: imageUrlField.document || ''
    };
  }
  if (typeof imageUrlField !== 'string') return { full: '', zoom: '', other: '', document: '' };
  if (imageUrlField.startsWith('{') && imageUrlField.endsWith('}')) {
    try {
      const parsed = JSON.parse(imageUrlField);
      return {
        full: parsed.full || '',
        zoom: parsed.zoom || '',
        other: parsed.other || '',
        document: parsed.document || ''
      };
    } catch (e) {
      return { full: imageUrlField, zoom: '', other: '', document: '' };
    }
  }
  return { full: imageUrlField, zoom: '', other: '', document: '' };
};

// Default status config if not provided
const defaultGetStatusConfig = (lotAmount, expDate) => {
  if (lotAmount <= 0) return { bg: '#795548', text: '#fff', label: 'หมด' };
  if (!expDate || expDate === '9999-12-31') return { bg: '#10b981', text: '#fff', label: 'ปกติ' };
  const today = new Date();
  const expire = new Date(expDate);
  const diffMonths = (expire - today) / (1000 * 60 * 60 * 24 * 30);
  if (expire <= today) return { bg: '#ef4444', text: '#fff', label: 'หมดอายุ' };
  if (diffMonths < 3) return { bg: '#f97316', text: '#fff', label: '< 3ด.' };
  if (diffMonths <= 12) return { bg: '#eab308', text: '#fff', label: '3ด.-1ปี' };
  return { bg: '#10b981', text: '#fff', label: 'ปกติ' };
};

// Thai Date Formatter
const formatThaiDateTime = (ts) => {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return String(ts);
    return d.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return String(ts);
  }
};

const SUB_WAREHOUSE_NAME = 'คลังย่อย (คลังห้องฟัน)';

// Clinic and storage destinations available for dispatch from sub-warehouse
const DISPENSE_DESTINATIONS = [
  'ห้องเก็บของ',
  'ห้องคลีนิก',
  '   - ห้อง sterile',
  '   - คลีนิกห้อง 1',
  '   - คลีนิกห้อง 2',
  '   - คลีนิกห้อง 3',
  '   - คลีนิกห้อง 4',
  '   - คลีนิกห้อง 5',
  '   - คลีนิกห้อง 6',
  '   - คลีนิกห้อง 7',
  '   - ห้อง OR',
  '   - ห้องอื่นๆ (PCU, ออกหน่วย)'
];

const SubWarehouseAdminPage = ({
  itemMaster = [],
  supplies = [],
  logs = [],
  supabase,
  user = {},
  isMobile = false,
  onNavigate = () => {},
  fetchData = async () => {},
  getStatusConfig = defaultGetStatusConfig,
  parseImages = defaultParseImages,
  setExpandedImage = () => {}
}) => {
  // Caretaker profile state
  const initialCaretakerName = user?.name || user?.user_metadata?.name || user?.email || 'ผู้ดูแลคลังย่อย';
  const [caretakerName, setCaretakerName] = useState(initialCaretakerName);

  // Active tab state: 'inbound', 'inventory', 'dispense', 'reports'
  const [activeTab, setActiveTab] = useState('inbound');

  // Loading and feedback states
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  // Inbound transfers list (fetched from Supabase or simulated)
  const [inboundTransfers, setInboundTransfers] = useState([]);
  const [outboundHistory, setOutboundHistory] = useState([]);
  const [qrItems, setQrItems] = useState([]);

  // ==========================================
  // Tab 1: Receive Confirmation Modal State
  // ==========================================
  const [receiveModal, setReceiveModal] = useState({
    isOpen: false,
    transfer: null,
    receiverName: caretakerName,
    notes: '',
    isSubmitting: false
  });

  // ==========================================
  // Tab 1 & 2: QR Tag Generator & Print Modal State
  // ==========================================
  const [qrPrintModal, setQrPrintModal] = useState({
    isOpen: false,
    item: null,
    quantity: 1,
    labelType: 'compact', // 'compact', 'full', 'blank_sheet'
    generatedCodes: [],
    isGenerating: false,
    isPrinting: false
  });

  // ==========================================
  // Tab 2: QR Code Scanner & Tracker State
  // ==========================================
  const [searchBarcode, setSearchBarcode] = useState('');
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [scannedQRData, setScannedQRData] = useState(null);
  const [qrScanError, setQrScanError] = useState(null);
  const [isUpdatingQRStatus, setIsUpdatingQRStatus] = useState(false);
  const [inventorySearchQuery, setInventorySearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');

  // ==========================================
  // Tab 3: Dispense Form State
  // ==========================================
  const [dispenseForm, setDispenseForm] = useState({
    selectedMasterId: '',
    destinationWarehouse: '   - คลีนิกห้อง 1',
    quantity: 1,
    selectedQRCodes: [],
    approvalNote: '',
    isSubmitting: false,
    errorMessage: null
  });

  // ==========================================
  // Tab 4: Reports State
  // ==========================================
  const [reportType, setReportType] = useState('current_stock'); // 'current_stock', 'inbound_history', 'dispense_history'

  // Print ref for thermal or A4 labels
  const printableAreaRef = useRef(null);

  // Sync caretakerName when user prop changes
  useEffect(() => {
    if (user?.name || user?.email) {
      const name = user.name || user.user_metadata?.name || user.email;
      if (name && caretakerName === 'ผู้ดูแลคลังย่อย') {
        setCaretakerName(name);
      }
    }
  }, [user, caretakerName]);

  // ==========================================
  // Load Inbound Transfers, QR Items, and History
  // ==========================================
  const loadSubWarehouseData = useCallback(async () => {
    setIsLoading(true);
    try {
      // 1. Fetch pending inbound transfers to sub-warehouse
      if (supabase) {
        const pending = await fetchConsumableTransfers({
          supabase,
          status: 'pending_sub_receipt',
          destination_warehouse: SUB_WAREHOUSE_NAME
        });
        setInboundTransfers(pending || []);

        // Fetch recent outbound transfers from sub-warehouse
        const outbound = await fetchConsumableTransfers({
          supabase,
          source_warehouse: SUB_WAREHOUSE_NAME
        });
        setOutboundHistory(outbound || []);

        // Fetch QR items registered at sub-warehouse
        const qrList = await fetchConsumableQRItems({
          supabase,
          location: SUB_WAREHOUSE_NAME
        });
        setQrItems(qrList || []);
      }
    } catch (err) {
      console.warn('loadSubWarehouseData error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadSubWarehouseData();
  }, [loadSubWarehouseData]);

  // Flash feedback toast
  const showToast = (message, type = 'success') => {
    setFeedbackMessage({ message, type });
    setTimeout(() => {
      setFeedbackMessage(null);
    }, 4000);
  };

  // ==========================================
  // Compute Sub-Warehouse Inventory & Master Items
  // ==========================================
  // Filter master items in consumable category
  const consumableMasterItems = useMemo(() => {
    return (itemMaster || []).filter(
      item => (item.category || '').trim() === 'วัสดุสิ้นเปลือง' && !item.is_set
    );
  }, [itemMaster]);

  // Current stock list in Sub-Warehouse
  const subWarehouseInventory = useMemo(() => {
    const subSupplies = (supplies || []).filter(
      s => s.warehouse_name === SUB_WAREHOUSE_NAME && Number(s.item_amount) > 0
    );

    return subSupplies.map(supply => {
      const master = consumableMasterItems.find(m => String(m.id) === String(supply.master_id)) || {};
      const amount = Number(supply.item_amount) || 0;
      const minStock = Number(master.min_stock) || 5;
      const expDate = master.exp_date || '';
      const statusConfig = getStatusConfig(amount, expDate);

      return {
        supplyId: supply.id,
        masterId: supply.master_id,
        master,
        itemName: master.item_name || 'ไม่ระบุชื่อวัสดุ',
        lotNumber: master.lot_number || '-',
        expDate: expDate || '-',
        unit: master.unit || 'ชิ้น',
        amount,
        minStock,
        specialty: master.specialty || 'ทันตกรรมทั่วไป/อื่นๆ',
        cabinet: master.cabinet || '-',
        shelf: master.shelf || '-',
        row: master.row || '-',
        imageUrl: master.image_url,
        statusConfig,
        isLowStock: amount <= minStock,
        isExpiringSoon: statusConfig.label === '< 3ด.' || statusConfig.label === 'หมดอายุ'
      };
    });
  }, [supplies, consumableMasterItems, getStatusConfig]);

  // ==========================================
  // Top KPI Metrics
  // ==========================================
  const kpiMetrics = useMemo(() => {
    const totalItems = subWarehouseInventory.length;
    const totalPieces = subWarehouseInventory.reduce((sum, item) => sum + item.amount, 0);
    const lowStockCount = subWarehouseInventory.filter(item => item.isLowStock).length;
    const expiringSoonCount = subWarehouseInventory.filter(item => item.isExpiringSoon).length;
    const pendingInboundCount = inboundTransfers.length;
    const taggedQRCount = qrItems.length;

    return {
      totalItems,
      totalPieces,
      lowStockCount,
      expiringSoonCount,
      pendingInboundCount,
      taggedQRCount
    };
  }, [subWarehouseInventory, inboundTransfers, qrItems]);

  // Filtered Inventory for Tab 2
  const filteredInventory = useMemo(() => {
    return subWarehouseInventory.filter(item => {
      // Specialty filter
      if (selectedSpecialty !== 'all') {
        const specMatch = item.specialty && (
          item.specialty.includes(selectedSpecialty) ||
          DENTAL_SPECIALTIES.some(s => s.id === selectedSpecialty && item.specialty.includes(s.name))
        );
        if (!specMatch) return false;
      }

      // Search query filter
      if (inventorySearchQuery.trim()) {
        const query = inventorySearchQuery.toLowerCase();
        const nameMatch = item.itemName.toLowerCase().includes(query);
        const lotMatch = (item.lotNumber || '').toLowerCase().includes(query);
        const aliasMatch = (item.master?.aliases || '').toLowerCase().includes(query);
        return nameMatch || lotMatch || aliasMatch;
      }

      return true;
    });
  }, [subWarehouseInventory, selectedSpecialty, inventorySearchQuery]);

  // ==========================================
  // Tab 1: Confirm Receipt Handler
  // ==========================================
  const handleOpenReceiveModal = (transfer) => {
    setReceiveModal({
      isOpen: true,
      transfer,
      receiverName: caretakerName || 'ผู้ดูแลคลังย่อย',
      notes: '',
      isSubmitting: false
    });
  };

  const handleConfirmReceive = async () => {
    if (!receiveModal.transfer) return;
    setReceiveModal(prev => ({ ...prev, isSubmitting: true }));

    try {
      await confirmSubWarehouseReceipt({
        supabase,
        transferId: receiveModal.transfer.id,
        transfer: receiveModal.transfer,
        receiverName: receiveModal.receiverName || caretakerName,
        notes: receiveModal.notes,
        supplies,
        fetchData
      });

      showToast(`✅ รับของเข้าคลังย่อยสำเร็จ: ${receiveModal.transfer.item_name} จำนวน ${receiveModal.transfer.quantity} ${receiveModal.transfer.unit || 'ชิ้น'}`);
      setReceiveModal({ isOpen: false, transfer: null, receiverName: caretakerName, notes: '', isSubmitting: false });
      await loadSubWarehouseData();
    } catch (err) {
      console.error('Error confirming receipt:', err);
      alert('เกิดข้อผิดพลาดในการรับของเข้าคลังย่อย: ' + err.message);
      setReceiveModal(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  // Demo: Create a test inbound transfer from main to sub-warehouse if list is empty
  const handleCreateDemoTransfer = async () => {
    if (consumableMasterItems.length === 0) {
      alert('ไม่พบรายการสินค้าวัสดุสิ้นเปลืองในระบบ');
      return;
    }
    const sampleItem = consumableMasterItems[0];
    try {
      await createConsumableTransfer({
        supabase,
        masterId: sampleItem.id,
        itemName: sampleItem.item_name,
        lotNumber: sampleItem.lot_number || 'LOT-DEMO-01',
        expDate: sampleItem.exp_date || '2027-12-31',
        quantity: 10,
        unit: sampleItem.unit || 'กล่อง',
        sourceWarehouse: 'คลังใหญ่ (คลังพัสดุ)',
        destinationWarehouse: SUB_WAREHOUSE_NAME,
        sentBy: 'คลังใหญ่ (คลังพัสดุ)',
        notes: 'เบิกจ่ายตามโควตาประจำสัปดาห์'
      });
      showToast('📥 สร้างรายการจำลองส่งของเข้าคลังย่อยเรียบร้อยแล้ว');
      await loadSubWarehouseData();
    } catch (err) {
      alert('ไม่สามารถสร้างรายการจำลองได้: ' + err.message);
    }
  };

  // ==========================================
  // Tab 1 & 2: QR Tag Modal Handlers
  // ==========================================
  const handleOpenQRModal = (itemOrTransfer) => {
    const qty = Number(itemOrTransfer.quantity || itemOrTransfer.amount || 1);
    const initialQty = Math.min(Math.max(qty, 1), 24);

    const generated = Array.from({ length: initialQty }, (_, i) => ({
      id: generateConsumableUID(i),
      qr_code: generateConsumableUID(i),
      item_name: itemOrTransfer.item_name || itemOrTransfer.itemName || 'วัสดุสิ้นเปลือง',
      lot_number: itemOrTransfer.lot_number || itemOrTransfer.lotNumber || '-',
      exp_date: itemOrTransfer.exp_date || itemOrTransfer.expDate || '-',
      unit: itemOrTransfer.unit || 'ชิ้น',
      specialty: itemOrTransfer.specialty || 'ทันตกรรมทั่วไป/อื่นๆ',
      current_location: SUB_WAREHOUSE_NAME,
      unit_price: itemOrTransfer.unit_price || 0
    }));

    setQrPrintModal({
      isOpen: true,
      item: itemOrTransfer,
      quantity: initialQty,
      labelType: 'compact',
      generatedCodes: generated,
      isGenerating: false,
      isPrinting: false
    });
  };

  const handleUpdateQRQuantity = (newQty) => {
    const count = Math.min(Math.max(Number(newQty) || 1, 1), 48);
    const generated = Array.from({ length: count }, (_, i) => ({
      id: generateConsumableUID(i),
      qr_code: generateConsumableUID(i),
      item_name: qrPrintModal.item?.item_name || qrPrintModal.item?.itemName || 'วัสดุสิ้นเปลือง',
      lot_number: qrPrintModal.item?.lot_number || qrPrintModal.item?.lotNumber || '-',
      exp_date: qrPrintModal.item?.exp_date || qrPrintModal.item?.expDate || '-',
      unit: qrPrintModal.item?.unit || 'ชิ้น',
      specialty: qrPrintModal.item?.specialty || 'ทันตกรรมทั่วไป/อื่นๆ',
      current_location: SUB_WAREHOUSE_NAME,
      unit_price: qrPrintModal.item?.unit_price || 0
    }));

    setQrPrintModal(prev => ({
      ...prev,
      quantity: count,
      generatedCodes: generated
    }));
  };

  // Save QR tags to database and trigger print
  const handleSaveAndPrintQRTags = async () => {
    if (!qrPrintModal.generatedCodes || qrPrintModal.generatedCodes.length === 0) return;
    setQrPrintModal(prev => ({ ...prev, isPrinting: true }));

    try {
      if (supabase) {
        const rowsToInsert = qrPrintModal.generatedCodes.map(code => ({
          qr_code: code.qr_code,
          master_id: qrPrintModal.item?.master_id || qrPrintModal.item?.masterId || null,
          item_name: code.item_name,
          specialty: code.specialty,
          lot_number: code.lot_number,
          exp_date: code.exp_date !== '-' ? code.exp_date : null,
          unit: code.unit,
          current_location: SUB_WAREHOUSE_NAME,
          status: 'active',
          label_type: qrPrintModal.labelType,
          caretaker_name: caretakerName,
          created_by: caretakerName,
          created_at: new Date().toISOString()
        }));

        const { error: insErr } = await supabase.from('consumable_items').insert(rowsToInsert);
        if (insErr) {
          console.warn('Save consumable_items warning:', insErr.message);
        }

        // Insert logs
        const logRows = qrPrintModal.generatedCodes.map(c => ({
          qr_code: c.qr_code,
          action: `สร้างฉลาก QR (${qrPrintModal.labelType}) ในคลังย่อย`,
          location: SUB_WAREHOUSE_NAME,
          actor_name: caretakerName,
          notes: `Lot: ${c.lot_number}, EXP: ${c.exp_date}`,
          timestamp: new Date().toISOString()
        }));
        await supabase.from('consumable_logs').insert(logRows);
      }

      showToast(`🏷️ บันทึกและพิมพ์แท็ก QR สำเร็จ ${qrPrintModal.generatedCodes.length} ดวง`);

      // Trigger Print
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.print();
        }, 300);
      }

      await loadSubWarehouseData();
      setQrPrintModal(prev => ({ ...prev, isPrinting: false, isOpen: false }));
    } catch (err) {
      console.error('Error saving QR tags:', err);
      alert('เกิดข้อผิดพลาดในการบันทึก QR: ' + err.message);
      setQrPrintModal(prev => ({ ...prev, isPrinting: false }));
    }
  };

  // ==========================================
  // Tab 2: QR Scanner & Status Tracking
  // ==========================================
  const handleLookupQRCode = async (codeToLookup) => {
    const targetCode = (codeToLookup || searchBarcode).trim();
    if (!targetCode) return;

    setQrScanError(null);
    setScannedQRData(null);

    try {
      if (supabase) {
        const { data, error } = await supabase
          .from('consumable_items')
          .select('*')
          .eq('qr_code', targetCode)
          .single();

        if (error || !data) {
          // Check if code matches an item master barcode or UID
          const matchedMaster = consumableMasterItems.find(
            m => m.id === targetCode || m.lot_number === targetCode
          );
          if (matchedMaster) {
            setScannedQRData({
              qr_code: targetCode,
              item_name: matchedMaster.item_name,
              lot_number: matchedMaster.lot_number,
              exp_date: matchedMaster.exp_date,
              current_location: SUB_WAREHOUSE_NAME,
              status: 'active',
              unit: matchedMaster.unit,
              isVirtual: true
            });
            return;
          }
          setQrScanError(`ไม่พบข้อมูล QR Code: "${targetCode}" ในระบบวัสดุสิ้นเปลือง`);
          return;
        }

        setScannedQRData(data);
      } else {
        // Fallback for tests or disconnected mode
        const found = qrItems.find(q => q.qr_code === targetCode);
        if (found) {
          setScannedQRData(found);
        } else {
          setScannedQRData({
            qr_code: targetCode,
            item_name: 'วัสดุทดสอบ QR Code',
            lot_number: 'LOT-TEST-99',
            exp_date: '2027-06-30',
            current_location: SUB_WAREHOUSE_NAME,
            status: 'active',
            unit: 'ชิ้น'
          });
        }
      }
    } catch (err) {
      console.error('QR Lookup error:', err);
      setQrScanError('เกิดข้อผิดพลาดในการค้นหา: ' + err.message);
    }
  };

  const handleChangeQRStatus = async (newStatus, targetLocation = null) => {
    if (!scannedQRData || !scannedQRData.qr_code) return;
    setIsUpdatingQRStatus(true);

    try {
      await updateConsumableItemStatus({
        supabase,
        qrCode: scannedQRData.qr_code,
        newStatus,
        newLocation: targetLocation || scannedQRData.current_location,
        actorName: caretakerName,
        notes: `ปรับปรุงสถานะเป็น '${newStatus}' โดย ${caretakerName}`
      });

      showToast(`🔄 ปรับปรุงสถานะ ${scannedQRData.qr_code} เป็น '${newStatus}' เรียบร้อย`);
      setScannedQRData(prev => ({
        ...prev,
        status: newStatus,
        current_location: targetLocation || prev.current_location,
        last_scanned_at: new Date().toISOString()
      }));
      await loadSubWarehouseData();
    } catch (err) {
      alert('ไม่สามารถอัปเดตสถานะ QR ได้: ' + err.message);
    } finally {
      setIsUpdatingQRStatus(false);
    }
  };

  // ==========================================
  // Tab 3: Dispense Form Handlers
  // ==========================================
  const selectedInventoryItem = useMemo(() => {
    if (!dispenseForm.selectedMasterId) return null;
    return subWarehouseInventory.find(i => String(i.masterId) === String(dispenseForm.selectedMasterId));
  }, [dispenseForm.selectedMasterId, subWarehouseInventory]);

  // QR tags available in sub-warehouse for the selected master item
  const availableQRTagsForSelectedMaster = useMemo(() => {
    if (!dispenseForm.selectedMasterId) return [];
    return qrItems.filter(
      q => String(q.master_id) === String(dispenseForm.selectedMasterId) &&
           q.current_location === SUB_WAREHOUSE_NAME &&
           q.status === 'active'
    );
  }, [dispenseForm.selectedMasterId, qrItems]);

  const handleApproveOutboundDispense = async (e) => {
    if (e) e.preventDefault();
    if (!selectedInventoryItem) {
      setDispenseForm(prev => ({ ...prev, errorMessage: 'กรุณาเลือกวัสดุที่ต้องการตัดจ่าย' }));
      return;
    }

    const qty = Number(dispenseForm.quantity);
    if (!qty || qty <= 0) {
      setDispenseForm(prev => ({ ...prev, errorMessage: 'กรุณาระบุจำนวนที่ต้องการเบิกจ่ายอย่างน้อย 1 ชิ้น' }));
      return;
    }

    if (qty > selectedInventoryItem.amount) {
      setDispenseForm(prev => ({
        ...prev,
        errorMessage: `จำนวนที่เบิก (${qty}) มากกว่ายอดคงเหลือในคลังย่อย (${selectedInventoryItem.amount} ${selectedInventoryItem.unit})`
      }));
      return;
    }

    setDispenseForm(prev => ({ ...prev, isSubmitting: true, errorMessage: null }));

    try {
      await approveAndDispenseFromSub({
        supabase,
        masterId: selectedInventoryItem.masterId,
        itemName: selectedInventoryItem.itemName,
        sourceWarehouse: SUB_WAREHOUSE_NAME,
        destinationWarehouse: dispenseForm.destinationWarehouse,
        quantity: qty,
        lotNumber: selectedInventoryItem.lotNumber,
        unit: selectedInventoryItem.unit,
        caretakerName: caretakerName || 'ผู้ดูแลคลังย่อย',
        notes: dispenseForm.approvalNote,
        qrCodes: dispenseForm.selectedQRCodes,
        fetchData
      });

      showToast(`✍️ อนุมัติตัดของสำเร็จ: เบิก ${selectedInventoryItem.itemName} (${qty} ${selectedInventoryItem.unit}) สู่ ${dispenseForm.destinationWarehouse}`);

      // Reset form
      setDispenseForm({
        selectedMasterId: '',
        destinationWarehouse: '   - คลีนิกห้อง 1',
        quantity: 1,
        selectedQRCodes: [],
        approvalNote: '',
        isSubmitting: false,
        errorMessage: null
      });

      await loadSubWarehouseData();
    } catch (err) {
      console.error('Dispense error:', err);
      setDispenseForm(prev => ({
        ...prev,
        isSubmitting: false,
        errorMessage: err.message || 'เกิดข้อผิดพลาดในการตัดจ่ายวัสดุ'
      }));
    }
  };

  // ==========================================
  // Tab 4: Sub-Warehouse Report Export & Print
  // ==========================================
  const reportRows = useMemo(() => {
    if (reportType === 'current_stock') {
      return subWarehouseInventory.map(item => ({
        col1: item.itemName,
        col2: item.lotNumber,
        col3: item.expDate,
        col4: `${item.amount} ${item.unit}`,
        col5: item.specialty,
        col6: item.statusConfig.label,
        statusColor: item.statusConfig.bg
      }));
    }
    if (reportType === 'inbound_history') {
      return (logs || [])
        .filter(l => (l.warehouse || '').includes('คลังย่อย') && (l.action || '').includes('รับของ'))
        .map(l => ({
          col1: l.item_name || '-',
          col2: formatThaiDateTime(l.created_at || l.timestamp),
          col3: l.warehouse || SUB_WAREHOUSE_NAME,
          col4: l.action || '-',
          col5: 'รับเข้าเรียบร้อย',
          col6: 'ปกติ',
          statusColor: '#10b981'
        }));
    }
    // Outbound clinic dispenses
    return (logs || [])
      .filter(l => (l.action || '').includes('เบิกจ่าย') || (l.action || '').includes('ตัดของ'))
      .map(l => ({
        col1: l.item_name || '-',
        col2: formatThaiDateTime(l.created_at || l.timestamp),
        col3: l.warehouse || '-',
        col4: l.action || '-',
        col5: 'อนุมัติตัดจ่าย',
        col6: 'เบิกสำเร็จ',
        statusColor: '#3b82f6'
      }));
  }, [reportType, subWarehouseInventory, logs]);

  const handleExportCSV = () => {
    if (reportRows.length === 0) {
      alert('ไม่มีข้อมูลสำหรับส่งออก CSV');
      return;
    }

    let headers = ['ชื่อวัสดุ', 'Lot Number', 'วันหมดอายุ', 'จำนวนคงคลัง', 'สาขาเฉพาะทาง', 'สถานะ'];
    if (reportType !== 'current_stock') {
      headers = ['ชื่อวัสดุ', 'วัน-เวลา', 'คลัง/ห้องปลายทาง', 'รายละเอียดการทำรายการ', 'ประเภทรายการ', 'สถานะ'];
    }

    const csvLines = [
      headers.join(','),
      ...reportRows.map(r => [
        `"${r.col1 || ''}"`,
        `"${r.col2 || ''}"`,
        `"${r.col3 || ''}"`,
        `"${r.col4 || ''}"`,
        `"${r.col5 || ''}"`,
        `"${r.col6 || ''}"`
      ].join(','))
    ];

    const csvContent = '\uFEFF' + csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `sub_warehouse_report_${reportType}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleQuickPrintReport = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-slate-100 p-3 sm:p-5 md:p-6 pb-24 font-sans">
      {/* Toast Feedback Notification */}
      {feedbackMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl bg-slate-900/95 border border-cyan-500/40 text-cyan-200 text-sm backdrop-blur-md animate-bounce">
          <span>{feedbackMessage.message}</span>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 1. TOP HEADER & CARETAKER PROFILE */}
      {/* ========================================================================= */}
      <header className="mb-6 bg-slate-900/80 border border-slate-800 backdrop-blur-md rounded-2xl p-4 sm:p-6 shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-3xl sm:text-4xl p-2 rounded-2xl bg-indigo-500/10 border border-indigo-500/30">🏬</span>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight bg-gradient-to-r from-cyan-400 via-sky-300 to-indigo-300 bg-clip-text text-transparent">
                  ศูนย์จัดการคลังย่อย (คลังห้องฟัน) - สำหรับผู้ดูแลคลังย่อย
                </h1>
                <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                  Sub-Warehouse Caretaker Center: รับเข้าจากคลังใหญ่, จัดการสต็อกเฉพาะทาง, สแกน QR Code และอนุมัติเบิกจ่ายสู่คลีนิก
                </p>
              </div>
            </div>
          </div>

          {/* Active Caretaker Profile & Fast Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Active Caretaker Input */}
            <div className="flex items-center gap-2 bg-slate-800/80 border border-slate-700/60 rounded-xl px-3 py-1.5 shadow-inner">
              <span className="text-xs text-indigo-300 font-medium">👤 ผู้ดูแลคลังย่อย:</span>
              <input
                type="text"
                value={caretakerName}
                onChange={(e) => setCaretakerName(e.target.value)}
                placeholder="ชื่อผู้ดูแลคลังย่อย"
                className="bg-transparent border-none text-xs sm:text-sm text-white font-semibold focus:outline-none focus:ring-1 focus:ring-cyan-400 rounded px-1 w-32 sm:w-40"
              />
            </div>

            {/* Fast Action Buttons */}
            <button
              onClick={() => setActiveTab('reports')}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-800/90 hover:bg-slate-700 text-slate-200 text-xs sm:text-sm font-medium rounded-xl border border-slate-700 transition"
              title="ดูเอกสารและรายงานคลังย่อย"
            >
              <span>📑</span>
              <span className="hidden sm:inline">รายงานคลังย่อย</span>
            </button>

            <button
              onClick={async () => {
                await loadSubWarehouseData();
                if (typeof fetchData === 'function') await fetchData();
                showToast('🔄 รีเฟรชข้อมูลคลังย่อยเรียบร้อยแล้ว');
              }}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-2 bg-cyan-950/60 hover:bg-cyan-900/80 text-cyan-300 text-xs sm:text-sm font-medium rounded-xl border border-cyan-800/50 transition disabled:opacity-50"
              title="รีเฟรชข้อมูลจากระบบ"
            >
              <span className={isLoading ? 'animate-spin' : ''}>🔄</span>
              <span className="hidden sm:inline">รีเฟรช</span>
            </button>

            <button
              onClick={() => onNavigate('consumable-dashboard')}
              className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white text-xs sm:text-sm font-semibold rounded-xl shadow-lg transition"
              title="กลับสู่หน้าแดชบอร์ดภาพรวม 4 คลัง"
            >
              <span>📊</span>
              <span>กลับหน้าแดชบอร์ดรวม</span>
            </button>
          </div>
        </div>
      </header>

      {/* ========================================================================= */}
      {/* 2. TOP KPI METRICS CARDS */}
      {/* ========================================================================= */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        {/* Card 1: สต็อกคงเหลือในคลังย่อย */}
        <div
          onClick={() => setActiveTab('inventory')}
          className="cursor-pointer bg-slate-900/70 hover:bg-slate-800/80 border border-slate-800 hover:border-cyan-500/40 rounded-2xl p-4 backdrop-blur-md transition shadow-lg group"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-400 group-hover:text-cyan-300">สต็อกคงเหลือคลังย่อย</span>
            <span className="text-lg p-1.5 rounded-lg bg-cyan-500/10 text-cyan-400">🏬</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-white">
            {kpiMetrics.totalItems} <span className="text-xs font-normal text-slate-400">รายการ</span>
          </div>
          <p className="text-xs text-cyan-400/90 mt-1 font-medium">
            รวม {kpiMetrics.totalPieces.toLocaleString()} ชิ้นในคลังห้องฟัน
          </p>
        </div>

        {/* Card 2: สินค้าใกล้หมดเกณฑ์ (Low Stock < Min) */}
        <div
          onClick={() => setActiveTab('inventory')}
          className="cursor-pointer bg-slate-900/70 hover:bg-slate-800/80 border border-slate-800 hover:border-amber-500/40 rounded-2xl p-4 backdrop-blur-md transition shadow-lg group"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-400 group-hover:text-amber-300">สินค้าใกล้หมดเกณฑ์</span>
            <span className="text-lg p-1.5 rounded-lg bg-amber-500/10 text-amber-400">⚠️</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-amber-400">
            {kpiMetrics.lowStockCount} <span className="text-xs font-normal text-slate-400">รายการ</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            ยอดคงเหลือ &lt; ค่า Min คลังย่อย
          </p>
        </div>

        {/* Card 3: สินค้าใกล้หมดอายุ (< 3 เดือน) */}
        <div
          onClick={() => setActiveTab('inventory')}
          className="cursor-pointer bg-slate-900/70 hover:bg-slate-800/80 border border-slate-800 hover:border-rose-500/40 rounded-2xl p-4 backdrop-blur-md transition shadow-lg group"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-400 group-hover:text-rose-300">ใกล้หมดอายุ (&lt; 3 ด.)</span>
            <span className="text-lg p-1.5 rounded-lg bg-rose-500/10 text-rose-400">⏳</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-rose-400">
            {kpiMetrics.expiringSoonCount} <span className="text-xs font-normal text-slate-400">รายการ</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            เร่งหมุนเวียน/นำไปใช้ในคลีนิก
          </p>
        </div>

        {/* Card 4: รอรับเข้าจากคลังใหญ่ (Pending Inbound) - Blinking/Pulsing badge if > 0 */}
        <div
          onClick={() => setActiveTab('inbound')}
          className={`cursor-pointer rounded-2xl p-4 backdrop-blur-md transition shadow-lg relative group ${
            kpiMetrics.pendingInboundCount > 0
              ? 'bg-amber-950/30 border-2 border-amber-400/80 hover:bg-amber-900/40 shadow-amber-500/20'
              : 'bg-slate-900/70 hover:bg-slate-800/80 border border-slate-800'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-300 group-hover:text-amber-300">รอรับเข้าจากคลังใหญ่</span>
            <span className="text-lg p-1.5 rounded-lg bg-amber-500/10 text-amber-400">📥</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl sm:text-2xl font-black text-white">
              {kpiMetrics.pendingInboundCount}
            </span>
            <span className="text-xs font-normal text-slate-400">รายการ</span>
            {kpiMetrics.pendingInboundCount > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500 text-slate-950 animate-pulse ml-auto">
                รอรับของ!
              </span>
            )}
          </div>
          <p className="text-xs text-amber-300/90 mt-1 font-medium">
            {kpiMetrics.pendingInboundCount > 0 ? 'กดเพื่อเปิดหน้าตรวจรับของ' : 'ไม่มีรายการค้างรับ'}
          </p>
        </div>

        {/* Card 5: ชิ้นที่ติดแท็ก QR Code แล้ว */}
        <div
          onClick={() => setActiveTab('inventory')}
          className="col-span-2 sm:col-span-1 cursor-pointer bg-slate-900/70 hover:bg-slate-800/80 border border-slate-800 hover:border-indigo-500/40 rounded-2xl p-4 backdrop-blur-md transition shadow-lg group"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-400 group-hover:text-indigo-300">ติดแท็ก QR Code แล้ว</span>
            <span className="text-lg p-1.5 rounded-lg bg-indigo-500/10 text-indigo-400">🏷️</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-indigo-300">
            {kpiMetrics.taggedQRCount} <span className="text-xs font-normal text-slate-400">ชิ้น</span>
          </div>
          <p className="text-xs text-indigo-400/90 mt-1">
            ในคลังย่อยพร้อมสแกนติดตาม
          </p>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3. INTERACTIVE 4 TABS NAVIGATION */}
      {/* ========================================================================= */}
      <nav className="flex items-center gap-2 p-1.5 bg-slate-900/90 border border-slate-800 rounded-2xl mb-6 backdrop-blur-md overflow-x-auto scrollbar-none">
        {/* Tab 1 */}
        <button
          onClick={() => setActiveTab('inbound')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition whitespace-nowrap ${
            activeTab === 'inbound'
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 shadow-md'
              : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
          }`}
        >
          <span>📥</span>
          <span>รอรับของจากคลังใหญ่</span>
          {kpiMetrics.pendingInboundCount > 0 && (
            <span className={`px-2 py-0.5 text-xs rounded-full font-extrabold ${
              activeTab === 'inbound' ? 'bg-slate-950 text-amber-400' : 'bg-amber-500 text-slate-950 animate-pulse'
            }`}>
              {kpiMetrics.pendingInboundCount}
            </span>
          )}
        </button>

        {/* Tab 2 */}
        <button
          onClick={() => setActiveTab('inventory')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition whitespace-nowrap ${
            activeTab === 'inventory'
              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
              : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
          }`}
        >
          <span>🏷️</span>
          <span>สต็อกคลังย่อย & สแกน QR</span>
          <span className="px-2 py-0.5 text-xs rounded-full bg-slate-800 text-cyan-300 font-semibold">
            {kpiMetrics.totalItems}
          </span>
        </button>

        {/* Tab 3 */}
        <button
          onClick={() => setActiveTab('dispense')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition whitespace-nowrap ${
            activeTab === 'dispense'
              ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md'
              : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
          }`}
        >
          <span>📤</span>
          <span>อนุมัติตัดของสู่คลีนิก</span>
        </button>

        {/* Tab 4 */}
        <button
          onClick={() => setActiveTab('reports')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition whitespace-nowrap ${
            activeTab === 'reports'
              ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
              : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
          }`}
        >
          <span>📑</span>
          <span>เอกสารรายงานคลังย่อย</span>
        </button>
      </nav>

      {/* ========================================================================= */}
      {/* 4. TAB 1: รายการรอรับของจากคลังใหญ่ (Pending Inbound & Acknowledgment) */}
      {/* ========================================================================= */}
      {activeTab === 'inbound' && (
        <section className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4">
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                <span>📥 รายการสินค้าที่ส่งมาจากคลังใหญ่ (รอตรวจรับเข้าคลังย่อย)</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                เมื่อตรวจรับสินค้าครบถ้วน ผู้ดูแลคลังย่อยสามารถกดยืนยันเพื่อนำเข้าสต็อกและพิมพ์ฉลาก QR Code ติดซอง/กล่องได้ทันที
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCreateDemoTransfer}
                className="text-xs px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition"
                title="สร้างรายการส่งของจำลองเพื่อทดสอบ"
              >
                ➕ จำลองส่งของจากคลังใหญ่
              </button>
            </div>
          </div>

          {inboundTransfers.length === 0 ? (
            <div className="bg-slate-900/50 border border-slate-800/80 rounded-2xl p-10 text-center text-slate-400">
              <span className="text-4xl mb-3 block">📦✨</span>
              <h3 className="text-base font-semibold text-slate-200">ไม่มีรายการค้างรับจากคลังใหญ่ในขณะนี้</h3>
              <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                เมื่อคลังใหญ่ทำการโอนย้ายสินค้ามายัง "คลังย่อย (คลังห้องฟัน)" รายการจะปรากฏที่นี่โดยอัตโนมัติ
              </p>
              <button
                onClick={handleCreateDemoTransfer}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-xs font-semibold rounded-xl border border-amber-500/40 transition"
              >
                <span>➕</span>
                <span>สร้างรายการส่งของจำลองเพื่อทดสอบระบบ</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inboundTransfers.map((item) => {
                const statusConf = getStatusConfig(item.quantity, item.exp_date);
                return (
                  <div
                    key={item.id || item.transfer_number}
                    className="bg-slate-900/80 border border-slate-800 hover:border-amber-500/40 rounded-2xl p-5 shadow-xl transition flex flex-col justify-between relative overflow-hidden group"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-full blur-xl pointer-events-none" />

                    <div>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-amber-400 border border-amber-500/20">
                          {item.transfer_number || 'TR-INBOUND'}
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-bold"
                          style={{ backgroundColor: statusConf.bg, color: statusConf.text }}
                        >
                          EXP: {item.exp_date || '-'}
                        </span>
                      </div>

                      <h3 className="text-base font-bold text-white group-hover:text-amber-300 transition line-clamp-2">
                        {item.item_name}
                      </h3>

                      <div className="grid grid-cols-2 gap-2 my-3 text-xs text-slate-300">
                        <div className="bg-slate-800/60 p-2 rounded-xl border border-slate-700/50">
                          <span className="text-slate-400 block text-[10px]">Lot Number</span>
                          <span className="font-semibold text-slate-200">{item.lot_number || '-'}</span>
                        </div>
                        <div className="bg-slate-800/60 p-2 rounded-xl border border-slate-700/50">
                          <span className="text-slate-400 block text-[10px]">จำนวนที่ส่ง</span>
                          <span className="font-bold text-amber-400 text-sm">
                            {item.quantity} {item.unit || 'ชิ้น'}
                          </span>
                        </div>
                      </div>

                      <div className="text-[11px] text-slate-400 space-y-1 mb-4">
                        <div className="flex items-center justify-between">
                          <span>🏢 ต้นทาง:</span>
                          <span className="text-slate-300 font-medium">{item.source_warehouse || 'คลังใหญ่ (คลังพัสดุ)'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>👤 ผู้ส่ง:</span>
                          <span className="text-slate-300 font-medium">{item.sent_by || '-'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>🕒 เวลาที่ส่ง:</span>
                          <span className="text-slate-300">{formatThaiDateTime(item.created_at)}</span>
                        </div>
                        {item.notes && (
                          <div className="text-slate-400 bg-slate-800/40 p-1.5 rounded-lg border border-slate-700/30">
                            📝 {item.notes}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 pt-3 border-t border-slate-800">
                      <button
                        onClick={() => handleOpenReceiveModal(item)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold rounded-xl shadow-lg transition"
                      >
                        <span>✅</span>
                        <span>รับของเข้าคลังย่อย</span>
                      </button>

                      <button
                        onClick={() => handleOpenQRModal(item)}
                        className="flex items-center justify-center gap-1 py-2.5 px-3 bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-300 text-xs font-semibold rounded-xl border border-indigo-500/40 transition"
                        title="สร้างและพิมพ์แท็ก QR Code สำหรับสินค้านี้"
                      >
                        <span>🏷️</span>
                        <span className="hidden sm:inline">พิมพ์ QR</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ========================================================================= */}
      {/* 5. TAB 2: สต็อกคลังย่อย & สแกน/ติดตาม QR Code (Inventory & QR Tracking) */}
      {/* ========================================================================= */}
      {activeTab === 'inventory' && (
        <section className="space-y-6">
          {/* TOOL: QR Code Scanner & Tracker */}
          <div className="bg-gradient-to-r from-slate-900 via-indigo-950/70 to-slate-900 border border-indigo-800/40 rounded-2xl p-5 shadow-2xl backdrop-blur-md">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                  <span>🔍 เครื่องมือสแกนและติดตามรหัส QR Code (QR Scanner & Tracker)</span>
                </h2>
                <p className="text-xs text-indigo-200/80 mt-0.5">
                  พิมพ์รหัสหรือยิงบาร์โค้ดสติ๊กเกอร์ (CON-...) เพื่อดูข้อมูลและเปลี่ยนสถานะ (In-Use ในคลีนิก / ตัดจ่าย Depleted)
                </p>
              </div>

              {/* QR Code Input & Buttons */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 sm:w-72">
                  <input
                    type="text"
                    value={searchBarcode}
                    onChange={(e) => setSearchBarcode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleLookupQRCode();
                    }}
                    placeholder="ป้อนรหัส CON-... หรือยิงบาร์โค้ด"
                    className="w-full bg-slate-800/90 border border-indigo-500/40 focus:border-cyan-400 rounded-xl px-3 py-2 text-xs sm:text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  />
                  {searchBarcode && (
                    <button
                      onClick={() => setSearchBarcode('')}
                      className="absolute right-2 top-2.5 text-xs text-slate-400 hover:text-white"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <button
                  onClick={() => handleLookupQRCode()}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs sm:text-sm font-semibold rounded-xl transition shadow"
                >
                  ค้นหา
                </button>

                <button
                  onClick={() => setIsCameraOpen(!isCameraOpen)}
                  className="px-3 py-2 bg-cyan-600/30 hover:bg-cyan-600/50 text-cyan-300 text-xs sm:text-sm font-semibold rounded-xl border border-cyan-500/40 transition flex items-center gap-1"
                >
                  <span>📷</span>
                  <span className="hidden sm:inline">{isCameraOpen ? 'ปิดกล้อง' : 'กล้องสแกน'}</span>
                </button>
              </div>
            </div>

            {/* Camera Scanner View */}
            {isCameraOpen && (
              <div className="mb-4 p-4 bg-slate-950/80 rounded-xl border border-cyan-500/40">
                <CameraScanner
                  initialMode="single"
                  onScanSuccess={(scannedText) => {
                    setSearchBarcode(scannedText);
                    setIsCameraOpen(false);
                    handleLookupQRCode(scannedText);
                  }}
                  onClose={() => setIsCameraOpen(false)}
                />
              </div>
            )}

            {/* QR Scan Error Message */}
            {qrScanError && (
              <div className="p-3 rounded-xl bg-rose-950/50 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2">
                <span>⚠️</span>
                <span>{qrScanError}</span>
              </div>
            )}

            {/* Scanned QR Result Display Card */}
            {scannedQRData && (
              <div className="mt-3 p-4 rounded-2xl bg-slate-950/70 border border-cyan-500/50 shadow-xl flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="text-3xl p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30">🏷️</span>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono font-bold text-cyan-300 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-800">
                        {scannedQRData.qr_code}
                      </span>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-bold ${
                        scannedQRData.status === 'in-use'
                          ? 'bg-amber-500 text-slate-950'
                          : scannedQRData.status === 'depleted'
                          ? 'bg-slate-700 text-slate-300'
                          : 'bg-emerald-500 text-slate-950'
                      }`}>
                        สถานะ: {scannedQRData.status === 'in-use' ? '🩺 กำลังใช้งานในคลีนิก' : scannedQRData.status === 'depleted' ? '🗑️ ตัดจ่ายแล้ว' : '✅ พร้อมใช้งาน (Active)'}
                      </span>
                      <span className="text-xs text-slate-400">
                        📍 สถานที่: {scannedQRData.current_location || SUB_WAREHOUSE_NAME}
                      </span>
                    </div>

                    <h3 className="text-base sm:text-lg font-bold text-white mt-1">
                      {scannedQRData.item_name}
                    </h3>

                    <div className="text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span>Lot: <strong className="text-slate-200">{scannedQRData.lot_number || '-'}</strong></span>
                      <span>EXP: <strong className="text-slate-200">{scannedQRData.exp_date || '-'}</strong></span>
                      <span>หน่วย: <strong className="text-slate-200">{scannedQRData.unit || 'ชิ้น'}</strong></span>
                      {scannedQRData.last_scanned_at && (
                        <span>สแกนล่าสุด: <strong className="text-slate-200">{formatThaiDateTime(scannedQRData.last_scanned_at)}</strong></span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Action Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    disabled={isUpdatingQRStatus || scannedQRData.status === 'in-use'}
                    onClick={() => handleChangeQRStatus('in-use', 'ห้องคลีนิก')}
                    className="px-3 py-2 bg-amber-600/80 hover:bg-amber-500 text-white text-xs font-semibold rounded-xl transition shadow disabled:opacity-40"
                  >
                    🩺 เปลี่ยนเป็น 'ใช้งานในคลีนิก'
                  </button>

                  <button
                    disabled={isUpdatingQRStatus || scannedQRData.status === 'depleted'}
                    onClick={() => handleChangeQRStatus('depleted')}
                    className="px-3 py-2 bg-rose-600/80 hover:bg-rose-500 text-white text-xs font-semibold rounded-xl transition shadow disabled:opacity-40"
                  >
                    🗑️ เปลี่ยนเป็น 'ตัดจ่ายแล้ว'
                  </button>

                  {scannedQRData.status !== 'active' && (
                    <button
                      disabled={isUpdatingQRStatus}
                      onClick={() => handleChangeQRStatus('active', SUB_WAREHOUSE_NAME)}
                      className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-semibold rounded-xl transition disabled:opacity-40"
                    >
                      📦 คืนเป็น 'พร้อมใช้'
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Specialty Filter Pills & Search */}
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 shadow-xl backdrop-blur-md">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <span className="absolute left-3 top-2.5 text-slate-400 text-sm">🔍</span>
                <input
                  type="text"
                  value={inventorySearchQuery}
                  onChange={(e) => setInventorySearchQuery(e.target.value)}
                  placeholder="ค้นหาชื่อวัสดุ, รหัสสินค้า, Lot Number..."
                  className="w-full pl-9 pr-4 py-2 bg-slate-800/80 border border-slate-700 focus:border-cyan-400 rounded-xl text-xs sm:text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                />
                {inventorySearchQuery && (
                  <button
                    onClick={() => setInventorySearchQuery('')}
                    className="absolute right-3 top-2.5 text-xs text-slate-400 hover:text-white"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="text-xs text-slate-400 font-medium">
                แสดงผล {filteredInventory.length} จาก {subWarehouseInventory.length} รายการในคลังย่อย
              </div>
            </div>

            {/* Specialty Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              <button
                onClick={() => setSelectedSpecialty('all')}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                  selectedSpecialty === 'all'
                    ? 'bg-cyan-500 text-slate-950 font-bold'
                    : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700'
                }`}
              >
                ทั้งหมด (All)
              </button>
              {DENTAL_SPECIALTIES.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedSpecialty(s.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                    selectedSpecialty === s.id
                      ? 'bg-cyan-500 text-slate-950 font-bold'
                      : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {s.name} ({s.code})
                </button>
              ))}
            </div>
          </div>

          {/* Sub-Warehouse Inventory Table & Cards */}
          {filteredInventory.length === 0 ? (
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-10 text-center text-slate-400">
              <span className="text-4xl mb-2 block">📭</span>
              <p className="text-sm font-semibold text-slate-200">ไม่พบรายการสินค้าที่ตรงกับเงื่อนไขการค้นหา</p>
              <p className="text-xs text-slate-500 mt-1">ลองเปลี่ยนคำค้นหาหรือตัวกรองสาขาเฉพาะทาง</p>
            </div>
          ) : (
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl shadow-xl overflow-hidden backdrop-blur-md">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs sm:text-sm">
                  <thead>
                    <tr className="bg-slate-950/70 border-b border-slate-800 text-slate-400 text-xs font-semibold uppercase tracking-wider">
                      <th className="p-3 sm:p-4">รูปภาพ</th>
                      <th className="p-3 sm:p-4">ชื่อวัสดุสิ้นเปลือง</th>
                      <th className="p-3 sm:p-4">สาขา / ตำแหน่ง</th>
                      <th className="p-3 sm:p-4">Lot / วันหมดอายุ</th>
                      <th className="p-3 sm:p-4 text-center">คงเหลือคลังย่อย</th>
                      <th className="p-3 sm:p-4 text-center">การจัดการ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {filteredInventory.map((item) => {
                      const img = parseImages(item.imageUrl);
                      return (
                        <tr
                          key={item.supplyId || item.masterId}
                          className="hover:bg-slate-800/40 transition"
                        >
                          {/* Image Thumbnail */}
                          <td className="p-3 sm:p-4 w-16">
                            {img.full ? (
                              <img
                                src={img.full}
                                alt={item.itemName}
                                onClick={() => setExpandedImage(img.full)}
                                className="w-12 h-12 object-cover rounded-xl border border-slate-700 cursor-pointer hover:opacity-80 transition"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700/60 flex items-center justify-center text-lg text-slate-500">
                                💊
                              </div>
                            )}
                          </td>

                          {/* Item Name */}
                          <td className="p-3 sm:p-4">
                            <div className="font-bold text-white text-sm hover:text-cyan-300 transition">
                              {item.itemName}
                            </div>
                            {item.master?.aliases && (
                              <div className="text-[11px] text-slate-400">
                                ชื่อเรียกอื่น: {item.master.aliases}
                              </div>
                            )}
                          </td>

                          {/* Specialty & Location */}
                          <td className="p-3 sm:p-4">
                            <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 mb-1">
                              {item.specialty}
                            </span>
                            <div className="text-[11px] text-slate-400">
                              ตู้: {item.cabinet} ชั้น: {item.shelf} แถว: {item.row}
                            </div>
                          </td>

                          {/* Lot & Expiry */}
                          <td className="p-3 sm:p-4">
                            <div className="font-mono text-slate-300 text-xs">
                              {item.lotNumber}
                            </div>
                            <span
                              className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold mt-1"
                              style={{ backgroundColor: item.statusConfig.bg, color: item.statusConfig.text }}
                            >
                              {item.expDate} ({item.statusConfig.label})
                            </span>
                          </td>

                          {/* Amount */}
                          <td className="p-3 sm:p-4 text-center">
                            <div className="text-base font-extrabold text-cyan-400">
                              {item.amount.toLocaleString()}{' '}
                              <span className="text-xs font-normal text-slate-400">{item.unit}</span>
                            </div>
                            {item.isLowStock && (
                              <span className="text-[10px] text-amber-400 font-semibold block">
                                ⚠️ ต่ำกว่า Min ({item.minStock})
                              </span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="p-3 sm:p-4 text-center">
                            <button
                              onClick={() => handleOpenQRModal(item)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-300 text-xs font-semibold rounded-xl border border-indigo-500/30 transition shadow"
                              title="สร้างและพิมพ์แท็ก QR Code เพิ่มเติมสำหรับสินค้านี้"
                            >
                              <span>➕</span>
                              <span>สร้างแท็ก QR</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ========================================================================= */}
      {/* 6. TAB 3: อนุมัติตัดของ & เบิกจ่ายสู่ห้องเก็บของ/คลีนิก (Caretaker Approved Outbound) */}
      {/* ========================================================================= */}
      {activeTab === 'dispense' && (
        <section className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Dispense Form (2 Columns) */}
            <div className="lg:col-span-2 bg-slate-900/80 border border-slate-800 rounded-2xl p-5 sm:p-6 shadow-xl backdrop-blur-md">
              <div className="border-b border-slate-800 pb-4 mb-5">
                <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                  <span>📤 แบบฟอร์มอนุมัติตัดของ & เบิกจ่ายจากคลังย่อย (Caretaker Dispense Form)</span>
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  สำหรับผู้ดูแลคลังย่อยอนุมัติเบิกจ่ายวัสดุสิ้นเปลืองไปยังห้องเก็บของ หรือห้องตรวจคลีนิกทันตกรรม (คลีนิก 1-7, OR, PCU)
                </p>
              </div>

              {dispenseForm.errorMessage && (
                <div className="mb-4 p-3 rounded-xl bg-rose-950/50 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2">
                  <span>⚠️</span>
                  <span>{dispenseForm.errorMessage}</span>
                </div>
              )}

              <form onSubmit={handleApproveOutboundDispense} className="space-y-4">
                {/* 1. Select Item from Sub-Warehouse */}
                <div>
                  <label htmlFor="dispense-select-item" className="block text-xs font-semibold text-slate-300 mb-1.5">
                    1. เลือกวัสดุสิ้นเปลืองในคลังย่อย <span className="text-rose-400">*</span>
                  </label>
                  <select
                    id="dispense-select-item"
                    aria-label="1. เลือกวัสดุสิ้นเปลืองในคลังย่อย"
                    value={dispenseForm.selectedMasterId}
                    onChange={(e) => {
                      setDispenseForm(prev => ({
                        ...prev,
                        selectedMasterId: e.target.value,
                        selectedQRCodes: [],
                        errorMessage: null
                      }));
                    }}
                    className="w-full bg-slate-800 border border-slate-700 focus:border-cyan-400 rounded-xl px-3 py-2.5 text-xs sm:text-sm text-white focus:outline-none"
                    required
                  >
                    <option value="">-- กรุณาเลือกวัสดุที่มีสต็อกในคลังย่อย --</option>
                    {subWarehouseInventory.map((item) => (
                      <option key={item.masterId} value={item.masterId}>
                        {item.itemName} (คงเหลือ: {item.amount} {item.unit} | Lot: {item.lotNumber})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Item Details Preview Box if selected */}
                {selectedInventoryItem && (
                  <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800 text-xs grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <span className="text-slate-400 block text-[10px]">คงเหลือคลังย่อย:</span>
                      <span className="text-cyan-400 font-bold text-sm">
                        {selectedInventoryItem.amount} {selectedInventoryItem.unit}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Lot Number:</span>
                      <span className="text-slate-200 font-semibold">{selectedInventoryItem.lotNumber}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">วันหมดอายุ:</span>
                      <span className="text-slate-200 font-semibold">{selectedInventoryItem.expDate}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">สาขา:</span>
                      <span className="text-indigo-300 font-semibold">{selectedInventoryItem.specialty}</span>
                    </div>
                  </div>
                )}

                {/* 2. Destination Warehouse */}
                <div>
                  <label htmlFor="dispense-select-destination" className="block text-xs font-semibold text-slate-300 mb-1.5">
                    2. ปลายทางที่ต้องการเบิกจ่าย <span className="text-rose-400">*</span>
                  </label>
                  <select
                    id="dispense-select-destination"
                    aria-label="2. ปลายทางที่ต้องการเบิกจ่าย"
                    value={dispenseForm.destinationWarehouse}
                    onChange={(e) => setDispenseForm(prev => ({ ...prev, destinationWarehouse: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 focus:border-cyan-400 rounded-xl px-3 py-2.5 text-xs sm:text-sm text-white focus:outline-none"
                    required
                  >
                    {DISPENSE_DESTINATIONS.map((dest) => (
                      <option key={dest} value={dest}>
                        {dest}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 3. Quantity to Dispense */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="dispense-input-quantity" className="text-xs font-semibold text-slate-300">
                      3. จำนวนที่ต้องการเบิกจ่าย <span className="text-rose-400">*</span>
                    </label>
                    {selectedInventoryItem && (
                      <span className="text-[11px] text-slate-400">
                        สูงสุดได้ไม่เกิน: <strong className="text-cyan-400">{selectedInventoryItem.amount}</strong> {selectedInventoryItem.unit}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="dispense-input-quantity"
                      aria-label="3. จำนวนที่ต้องการเบิกจ่าย"
                      type="number"
                      min="1"
                      max={selectedInventoryItem ? selectedInventoryItem.amount : 9999}
                      value={dispenseForm.quantity}
                      onChange={(e) => setDispenseForm(prev => ({ ...prev, quantity: e.target.value }))}
                      className="w-32 bg-slate-800 border border-slate-700 focus:border-cyan-400 rounded-xl px-3 py-2.5 text-xs sm:text-sm text-white font-bold focus:outline-none"
                      required
                    />
                    <span className="text-xs text-slate-300">
                      {selectedInventoryItem ? selectedInventoryItem.unit : 'ชิ้น'}
                    </span>
                  </div>
                </div>

                {/* 4. Optional: Select Specific QR Codes to Dispense */}
                {availableQRTagsForSelectedMaster.length > 0 && (
                  <div className="p-3.5 rounded-xl bg-indigo-950/20 border border-indigo-900/50">
                    <label className="block text-xs font-semibold text-indigo-300 mb-2">
                      🏷️ เลือก QR Code ประจำชิ้นที่เบิกจ่าย (มีแท็กในคลังย่อย {availableQRTagsForSelectedMaster.length} ชิ้น):
                    </label>
                    <div className="max-h-32 overflow-y-auto space-y-1.5 scrollbar-thin">
                      {availableQRTagsForSelectedMaster.map(qr => {
                        const isChecked = dispenseForm.selectedQRCodes.includes(qr.qr_code);
                        return (
                          <label
                            key={qr.qr_code}
                            className={`flex items-center gap-2 p-1.5 rounded-lg text-xs cursor-pointer transition ${
                              isChecked ? 'bg-indigo-600/30 text-indigo-200 font-semibold' : 'text-slate-400 hover:bg-slate-800'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setDispenseForm(prev => ({
                                    ...prev,
                                    selectedQRCodes: [...prev.selectedQRCodes, qr.qr_code]
                                  }));
                                } else {
                                  setDispenseForm(prev => ({
                                    ...prev,
                                    selectedQRCodes: prev.selectedQRCodes.filter(c => c !== qr.qr_code)
                                  }));
                                }
                              }}
                              className="rounded border-slate-700 text-indigo-500 focus:ring-0"
                            />
                            <span className="font-mono">{qr.qr_code}</span>
                            <span className="text-[10px] text-slate-400">({qr.lot_number || '-'})</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 5. Caretaker Signature & Notes */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      ผู้ลงนามอนุมัติ (Caretaker Signature) <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={caretakerName}
                      onChange={(e) => setCaretakerName(e.target.value)}
                      placeholder="ชื่อผู้ดูแลคลังย่อย"
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs sm:text-sm text-white focus:outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      หมายเหตุการเบิก / Case เคสการรักษา
                    </label>
                    <input
                      type="text"
                      value={dispenseForm.approvalNote}
                      onChange={(e) => setDispenseForm(prev => ({ ...prev, approvalNote: e.target.value }))}
                      placeholder="เช่น เบิกเตรียมเคสผ่าฟันคุด, เติมห้องตรวจ 1"
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs sm:text-sm text-white focus:outline-none"
                    />
                  </div>
                </div>

                {/* Submit Dispense Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={dispenseForm.isSubmitting || !selectedInventoryItem}
                    className="w-full py-3 px-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white text-sm font-bold rounded-xl shadow-lg transition disabled:opacity-40 flex items-center justify-center gap-2"
                  >
                    <span>✍️</span>
                    <span>{dispenseForm.isSubmitting ? 'กำลังบันทึกการตัดของ...' : 'อนุมัติและตัดของออกจากคลังย่อย'}</span>
                  </button>
                </div>
              </form>
            </div>

            {/* Quick Helper / Guidelines Box (1 Column) */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-xl backdrop-blur-md flex flex-col justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-1.5">
                  <span>💡</span>
                  <span>ข้อปฏิบัติการเบิกจ่ายคลังย่อย</span>
                </h3>
                <ul className="text-xs text-slate-400 space-y-2.5">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">•</span>
                    <span><strong>หลัก FEFO:</strong> ตรวจสอบวันหมดอายุ (Exp Date) ให้จ่ายของที่หมดอายุก่อนเสมอ</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">•</span>
                    <span><strong>ห้องคลีนิก:</strong> การจ่ายของเข้าคลีนิก ระบบจะปรับสถานะ QR Code เป็น <em>'in-use (กำลังใช้งาน)'</em> โดยอัตโนมัติ</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">•</span>
                    <span><strong>การตัดจ่ายทันที:</strong> หากของชิ้นนั้นถูกใช้งานหมดแล้ว สามารถสแกนและเปลี่ยนสถานะเป็น <em>'depleted'</em> ใน Tab 2 ได้</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">•</span>
                    <span><strong>ยอดคงคลัง:</strong> ระบบจะตัดสต็อกคลังย่อยและบันทึกประวัติการเบิกจ่ายพร้อมชื่อผู้ลงนามทันที</span>
                  </li>
                </ul>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-800/80 text-xs text-slate-500">
                ระบบจัดการคลังย่อยทันตกรรม CSSD Fang Hospital
              </div>
            </div>
          </div>

          {/* Outbound Transfer History Table */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-xl backdrop-blur-md">
            <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
              <span>📋 ประวัติรายการเบิกจ่ายออกจากคลังย่อยล่าสุด</span>
            </h3>

            {outboundHistory.length === 0 ? (
              <div className="text-center p-6 text-slate-500 text-xs">
                ยังไม่มีประวัติการเบิกจ่ายออกจากคลังย่อย
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-950/60 border-b border-slate-800 text-slate-400 font-semibold uppercase">
                      <th className="p-3">รหัสโอนย้าย</th>
                      <th className="p-3">วัน-เวลา</th>
                      <th className="p-3">ชื่อวัสดุ</th>
                      <th className="p-3">จำนวน</th>
                      <th className="p-3">ปลายทาง</th>
                      <th className="p-3">ผู้อนุมัติ</th>
                      <th className="p-3">สถานะ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {outboundHistory.map(tr => (
                      <tr key={tr.id || tr.transfer_number} className="hover:bg-slate-800/30">
                        <td className="p-3 font-mono text-cyan-400 font-medium">{tr.transfer_number}</td>
                        <td className="p-3 text-slate-400">{formatThaiDateTime(tr.created_at)}</td>
                        <td className="p-3 font-semibold text-white">{tr.item_name}</td>
                        <td className="p-3 text-amber-300 font-bold">{tr.quantity} {tr.unit}</td>
                        <td className="p-3 text-slate-300">{tr.destination_warehouse}</td>
                        <td className="p-3 text-slate-400">{tr.sent_by || '-'}</td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                            อนุมัติแล้ว
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ========================================================================= */}
      {/* 7. TAB 4: เอกสารรายงานคลังย่อย (Sub-Warehouse Reports) */}
      {/* ========================================================================= */}
      {activeTab === 'reports' && (
        <section className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/80 border border-slate-800 rounded-2xl p-4 sm:p-5 shadow-xl backdrop-blur-md">
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                <span>📑 เอกสารและรายงานคลังย่อย (คลังห้องฟัน)</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                สรุปสต็อกคงคลังย่อย, ประวัติการรับเข้าจากคลังใหญ่ และประวัติการจ่ายของสู่ห้องคลีนิก
              </p>
            </div>

            {/* Print & Export CSV buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleQuickPrintReport}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs sm:text-sm font-semibold rounded-xl border border-slate-700 transition shadow"
              >
                <span>🖨️</span>
                <span>พิมพ์รายงาน A4</span>
              </button>

              <button
                onClick={handleExportCSV}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-700 hover:bg-emerald-600 text-white text-xs sm:text-sm font-semibold rounded-xl transition shadow"
              >
                <span>📥</span>
                <span>ส่งออก CSV</span>
              </button>
            </div>
          </div>

          {/* Report Type Selector */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
            <button
              onClick={() => setReportType('current_stock')}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition ${
                reportType === 'current_stock'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700'
              }`}
            >
              📊 1. รายงานสต็อกคงเหลือคลังย่อยปัจจุบัน
            </button>
            <button
              onClick={() => setReportType('inbound_history')}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition ${
                reportType === 'inbound_history'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700'
              }`}
            >
              📥 2. ประวัติการรับเข้าจากคลังใหญ่
            </button>
            <button
              onClick={() => setReportType('dispense_history')}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition ${
                reportType === 'dispense_history'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700'
              }`}
            >
              📤 3. ประวัติการเบิกจ่ายสู่คลีนิก
            </button>
          </div>

          {/* Printable Report Document Card */}
          <div
            ref={printableAreaRef}
            className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-2xl backdrop-blur-md"
          >
            {/* Header Document */}
            <div className="border-b border-slate-800 pb-4 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h3 className="text-lg font-bold text-white">
                  {reportType === 'current_stock'
                    ? 'แบบรายงานสต็อกวัสดุสิ้นเปลืองคงคลังย่อย (คลังห้องฟัน)'
                    : reportType === 'inbound_history'
                    ? 'บันทึกประวัติการรับของเข้าคลังย่อยจากคลังใหญ่'
                    : 'บันทึกประวัติการเบิกจ่ายวัสดุสิ้นเปลืองสู่ห้องคลีนิก'}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  โรงพยาบาลฝาง ฝ่ายทันตกรรม | ผู้ดูแลคลังย่อย: {caretakerName} | วันที่ออกรายงาน: {new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <div className="text-xs text-slate-400 bg-slate-800/80 px-3 py-1.5 rounded-xl border border-slate-700">
                รวมทั้งหมด <strong className="text-cyan-400">{reportRows.length}</strong> แถว
              </div>
            </div>

            {/* Table */}
            {reportRows.length === 0 ? (
              <div className="text-center p-8 text-slate-500 text-xs">
                ไม่มีข้อมูลรายงานในหมวดนี้
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-950/80 border-b border-slate-800 text-slate-400 uppercase font-semibold">
                      <th className="p-3">ลำดับ</th>
                      <th className="p-3">รายการวัสดุ</th>
                      <th className="p-3">{reportType === 'current_stock' ? 'Lot Number' : 'วัน-เวลา'}</th>
                      <th className="p-3">{reportType === 'current_stock' ? 'วันหมดอายุ' : 'คลัง/ห้อง'}</th>
                      <th className="p-3 text-center">{reportType === 'current_stock' ? 'จำนวนคงคลัง' : 'รายละเอียด'}</th>
                      <th className="p-3 text-center">สถานะ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {reportRows.map((r, idx) => (
                      <tr key={idx} className="hover:bg-slate-800/30">
                        <td className="p-3 text-slate-500 w-12">{idx + 1}</td>
                        <td className="p-3 font-semibold text-white">{r.col1}</td>
                        <td className="p-3 text-slate-300 font-mono">{r.col2}</td>
                        <td className="p-3 text-slate-400">{r.col3}</td>
                        <td className="p-3 text-center font-bold text-cyan-300">{r.col4}</td>
                        <td className="p-3 text-center">
                          <span
                            className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold"
                            style={{ backgroundColor: r.statusColor, color: '#ffffff' }}
                          >
                            {r.col6}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ========================================================================= */}
      {/* 8. MODAL: RECEIVE INBOUND CONFIRMATION (Tab 1 Action) */}
      {/* ========================================================================= */}
      {receiveModal.isOpen && receiveModal.transfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full p-6 shadow-2xl relative">
            <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
              <span>✅ ยืนยันรับของเข้าคลังย่อย (คลังห้องฟัน)</span>
            </h3>
            <p className="text-xs text-slate-400 mb-4">
              ตรวจสอบข้อมูลสินค้าให้ถูกต้องก่อนกดยืนยันรับเข้า ยอดสต็อกคลังย่อยจะเพิ่มขึ้นทันที
            </p>

            {/* Transfer Item Summary */}
            <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800 text-xs space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-slate-400">ชื่อสินค้า:</span>
                <span className="font-bold text-white">{receiveModal.transfer.item_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">จำนวนที่รับ:</span>
                <span className="font-bold text-emerald-400 text-sm">
                  {receiveModal.transfer.quantity} {receiveModal.transfer.unit || 'ชิ้น'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Lot Number:</span>
                <span className="font-mono text-slate-200">{receiveModal.transfer.lot_number || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">วันหมดอายุ (EXP):</span>
                <span className="font-semibold text-amber-300">{receiveModal.transfer.exp_date || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">ส่งมาจาก:</span>
                <span className="text-slate-300">{receiveModal.transfer.source_warehouse || 'คลังใหญ่'}</span>
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  ชื่อผู้รับของ (ผู้ดูแลคลังย่อย) <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  value={receiveModal.receiverName}
                  onChange={(e) => setReceiveModal(prev => ({ ...prev, receiverName: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs sm:text-sm text-white focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  หมายเหตุการตรวจรับ (สภาพกล่อง/บรรจุภัณฑ์)
                </label>
                <textarea
                  value={receiveModal.notes}
                  onChange={(e) => setReceiveModal(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="เช่น สภาพกล่องสมบูรณ์ ไม่มีรอยฉีกขาด"
                  rows={2}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs sm:text-sm text-white focus:outline-none resize-none"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setReceiveModal({ isOpen: false, transfer: null, receiverName: caretakerName, notes: '', isSubmitting: false })}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs sm:text-sm rounded-xl transition"
              >
                ยกเลิก
              </button>

              <button
                type="button"
                onClick={handleConfirmReceive}
                disabled={receiveModal.isSubmitting}
                className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs sm:text-sm font-bold rounded-xl shadow-lg transition disabled:opacity-50"
              >
                {receiveModal.isSubmitting ? 'กำลังบันทึก...' : 'ยืนยันรับของเข้าคลังย่อย'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 9. MODAL: QR TAG GENERATION & PRINT PREVIEW (Tab 1 & 2 Action) */}
      {/* ========================================================================= */}
      {qrPrintModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full p-5 sm:p-6 shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                  <span>🏷️ สร้าง & พิมพ์แท็ก QR Code (Consumable QR Labels)</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  สร้างรหัส CON-... บันทึกลงระบบ และพิมพ์ฉลากความร้อนสำหรับติดวัสดุสิ้นเปลือง
                </p>
              </div>
              <button
                onClick={() => setQrPrintModal(prev => ({ ...prev, isOpen: false }))}
                className="text-slate-400 hover:text-white text-lg p-1"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto flex-1 pr-1">
              {/* Item Info Header */}
              <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 text-xs flex justify-between items-center">
                <div>
                  <span className="text-slate-400">วัสดุ:</span>{' '}
                  <strong className="text-white text-sm">
                    {qrPrintModal.item?.item_name || qrPrintModal.item?.itemName}
                  </strong>
                </div>
                <div className="text-slate-400">
                  Lot: <strong className="text-slate-200">{qrPrintModal.item?.lot_number || qrPrintModal.item?.lotNumber || '-'}</strong>
                </div>
              </div>

              {/* Label Options Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    จำนวนดวงที่ต้องการสร้าง
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="48"
                    value={qrPrintModal.quantity}
                    onChange={(e) => handleUpdateQRQuantity(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs sm:text-sm text-white font-bold focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    รูปแบบฉลากสติ๊กเกอร์
                  </label>
                  <select
                    value={qrPrintModal.labelType}
                    onChange={(e) => setQrPrintModal(prev => ({ ...prev, labelType: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs sm:text-sm text-white focus:outline-none"
                  >
                    <option value="compact">ฉลากย่อ Mini Label (ConsumableCompactLabel)</option>
                    <option value="full">ฉลากเต็ม Full Label (ConsumableFullLabel)</option>
                    <option value="blank_sheet">แผ่นสติ๊กเกอร์ 8 ดวง (BlankConsumableSheet)</option>
                  </select>
                </div>
              </div>

              {/* Live Preview Container */}
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-2">
                  ตัวอย่างฉลาก (Live Preview - แสดงดวงแรก):
                </label>
                <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex items-center justify-center overflow-auto max-h-72">
                  {qrPrintModal.labelType === 'compact' && (
                    <ConsumableCompactLabel
                      data={qrPrintModal.generatedCodes[0] || {}}
                      previewMode={true}
                    />
                  )}
                  {qrPrintModal.labelType === 'full' && (
                    <ConsumableFullLabel
                      data={qrPrintModal.generatedCodes[0] || {}}
                      previewMode={true}
                    />
                  )}
                  {qrPrintModal.labelType === 'blank_sheet' && (
                    <BlankConsumableSheet
                      items={qrPrintModal.generatedCodes}
                      previewMode={true}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="pt-4 mt-3 border-t border-slate-800 flex items-center justify-between gap-3">
              <span className="text-xs text-slate-400">
                พร้อมพิมพ์ <strong>{qrPrintModal.generatedCodes.length}</strong> ดวง
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setQrPrintModal(prev => ({ ...prev, isOpen: false }))}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs sm:text-sm rounded-xl transition"
                >
                  ปิด
                </button>

                <button
                  type="button"
                  onClick={handleSaveAndPrintQRTags}
                  disabled={qrPrintModal.isPrinting}
                  className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 text-white text-xs sm:text-sm font-bold rounded-xl shadow-lg transition disabled:opacity-50 flex items-center gap-1.5"
                >
                  <span>🖨️</span>
                  <span>{qrPrintModal.isPrinting ? 'กำลังส่งพิมพ์...' : 'บันทึก & สั่งพิมพ์ฉลาก'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubWarehouseAdminPage;
